import isJson from './isJson';
import {
  defaultWidgetSetting,
  PRECHAT_CUSTOMER_CHAT_AS
} from '@functions/const/defaultWidgetSetting';
import {flatten, isEmpty} from '@avada/utils';
import convertLocale from '@functions/helpers/i18n/convertLocale';
import merge from '@functions/helpers/utils/merge';
import {prepareOrderContactMethod} from '@assets/helpers/utils/prepareDefaultSettings';
import prepareEnableDisclaimerConsent from '@functions/helpers/disclaimerConsentHelper';

/**
 * @returns {{categories: App.Category[], questions: App.Question[], translation: *, widgetSetting: App.WidgetSetting}}
 */
const faqGlobal = () => {
  const {translation = {}} = window.AVADA_FAQ;
  const {languages = []} = translation;
  languages.push(convertLocale(window.Shopify.locale));
  return {
    categories: [],
    questions: [],
    campaigns: [],
    translation: {...translation, languages: [...new Set(languages)].filter(Boolean)},
    widgetSetting: {},
    ...window.AVADA_FAQ
  };
};

/**
 * @returns {string}
 */
function checkSelectedLanguage() {
  const {translation} = faqGlobal();
  const locale = convertLocale() || 'en';
  return translation[locale] ? locale : translation.primaryLanguage;
}

/**
 * @param {Object} translation
 * @returns {[]}
 */
export function prepareTranslatedFaqs() {
  const {categories, translation} = faqGlobal();
  const publishedCategories = categories.filter(x => x.published);
  const translatedCategories = translation[checkSelectedLanguage()]?.FAQs;

  if (isEmpty(translatedCategories)) return publishedCategories;

  try {
    return publishedCategories.map(c => {
      const {questions: rawQuestions, categoryId} = c;
      const {questions: transQuestions = [], ...category} = translatedCategories[categoryId] || c;
      const questions = rawQuestions.map(
        q => transQuestions.find(x => x.questionId === q.questionId) || q
      );
      return {...category, questions};
    });
  } catch (e) {
    console.error(e);
    return [];
  }
}

/**
 *
 * @param baseCategory
 * @param translatedCategory
 * @returns {*}
 */
export function mergeCategoryWithTranslations(baseCategory, translatedCategory) {
  const mergedCategory = {
    ...baseCategory,
    questions: baseCategory.questions.map(el => ({
      ...el,
      originalQuestion: el.question,
      originalAnswer: el.answer,
      categoryId: baseCategory.categoryId,
      title: el.question,
      shopId: baseCategory.shopId
    }))
  };

  return merge(mergedCategory, translatedCategory);
}

/**
 * @returns {[]}
 */
export function prepareTranslatedQuestion() {
  const {categories, translation} = faqGlobal();
  const publishedCategories = categories.filter(x => x.published);
  const translatedCategories = translation[checkSelectedLanguage()]?.FAQs || {};

  try {
    const result = publishedCategories.map(c => {
      const {questions: rawQuestions, categoryId} = c;
      const {questions: tranQuestions} = mergeCategoryWithTranslations(
        c,
        translatedCategories[categoryId]
      );
      const publishedQuestions = rawQuestions.filter(x => x.published);
      return publishedQuestions.map(q => {
        return tranQuestions.find(x => x.questionId === q.questionId) || q;
      });
    });
    return flatten(result);
  } catch (e) {
    console.error(e);
    return [];
  }
}

/**
 * @returns {[]}
 */
export function prepareTranslatedFeaturedQuestion() {
  const {categories, questions, translation} = faqGlobal();
  const translatedCategories = translation[checkSelectedLanguage()]?.FAQs || {};

  if (isEmpty(translatedCategories)) {
    return questions.filter(x => {
      const category = categories.find(c => c.categoryId === x.categoryId);
      const question = category?.questions.find(q => q.questionId === x.questionId) || x;
      return question.published && question.featured;
    });
  }

  try {
    const publishedCategories = categories.filter(x => x.published && x.questions.length);
    const result = publishedCategories.map(c => {
      const {questions: rawQuestions, categoryId} = c;
      const {questions: transQuestions = []} = translatedCategories[categoryId] || c;
      const featuredQuestions = rawQuestions.filter(x => x.published && x.featured);
      return featuredQuestions.map(
        q => transQuestions.find(x => x.questionId === q.questionId) || q
      );
    });
    return flatten(result);
  } catch (e) {
    console.error(e);
    return [];
  }
}

/**
 * @link https://trello.com/c/AehxHBWC Fallback if Shopify format the JSON wrong
 *
 * @returns {*[]}
 */
export function prepareCategories() {
  const {categories} = faqGlobal();
  const parsedCategories = categories.map(category => {
    const {questions} = category;
    if (!isJson(questions)) return category;
    return {...category, questions: JSON.parse(questions)};
  });
  return parsedCategories.filter(c => c.published && c.questions.filter(q => q.published).length);
}

/**
 * @param {Array} questions
 * @returns {*[]}
 */
export function prepareQuestions() {
  const {questions, categories} = faqGlobal();

  const publishedChunks = categories.map(category => {
    if (!category.published) return [];
    return category.questions.filter(q => q.published).map(q => q.questionId);
  });

  const publishedQuestions = flatten(publishedChunks);
  return publishedQuestions.map(id => questions?.find(q => q.questionId === id)).filter(Boolean);
}

/**
 * @returns {*[]}
 */
export function prepareFeaturedQuestions() {
  const {categories, questions} = faqGlobal();

  const publishedChunks = categories.map(category => {
    if (!category.published) return [];
    return category.questions.filter(q => q.published && q.featured).map(q => q.questionId);
  });

  const publishedQuestions = flatten(publishedChunks);
  return publishedQuestions.map(id => questions?.find(q => q.questionId === id)).filter(Boolean);
}

/**
 * @returns {*}
 */
export function prepareWidgetSetting() {
  const {widgetSetting: settings} = faqGlobal();

  if (!settings.headingTextFont) {
    settings.headingTextFont = settings.contentTextFont || 'Inter';
  }
  const newData = {
    ...defaultWidgetSetting,
    ...settings,
    orderContactMethod:
      settings.orderContactMethod ||
      prepareOrderContactMethod({...defaultWidgetSetting.contactMethod, ...settings.contactMethod}),
    welcomePopupSetting: {
      ...defaultWidgetSetting.welcomePopupSetting,
      ...(settings.welcomePopupSetting.welcomeMessage && {
        onlineMessage: settings.welcomePopupSetting?.welcomeMessage,
        offlineMessage: settings.welcomePopupSetting?.welcomeMessage
      }),
      ...settings.welcomePopupSetting
    },
    aiAssistantSettings: {
      ...defaultWidgetSetting.aiAssistantSettings,
      ...settings.aiAssistantSettings
    },
    preChatCustomerChatAs: (() => {
      if (settings.hasOwnProperty('preChatCustomerChatAs')) {
        return settings.preChatCustomerChatAs;
      }
      if (settings.hasOwnProperty('isChatWithAnonymous')) {
        return settings.isChatWithAnonymous
          ? PRECHAT_CUSTOMER_CHAT_AS.BOTH
          : PRECHAT_CUSTOMER_CHAT_AS.GUEST;
      }
      return defaultWidgetSetting.preChatCustomerChatAs;
    })(),
    isEnableDisclaimerConsent: prepareEnableDisclaimerConsent(settings)
  };

  if (!newData.quickReplySettings.suggestionFAQs) {
    newData.quickReplySettings.suggestionFAQs =
      defaultWidgetSetting.quickReplySettings.suggestionFAQs;
  }
  // to migrate data require default email field pre chat form
  if (
    !newData.requiredEmailFieldPreChatForm &&
    !newData.requiredNameFieldPreChatForm &&
    !newData.requiredPhoneFieldPreChatForm
  ) {
    newData.requiredEmailFieldPreChatForm = true;
  }

  return newData;
}

export function prepareCampaignSettings() {
  const {campaigns} = faqGlobal();
  return campaigns.filter(c => c.status === 'active').sort((a, b) => a.priority - b.priority);
}

/**
 * @param {function} postData
 * @returns {Promise<void>}
 */
export async function prepareMetafields(postData) {
  const websiteId = window.AVADA_APP_WEBSITE_ID;

  if (window.AVADA_FAQ?.fetchMetafields || (!window.AVADA_FAQ?.isApiMetafield && !websiteId))
    return;

  const {chattySat, shopifyDomain} = window.AVADA_APP_FAQS_STATUS;
  const {translation} = faqGlobal();
  const {shop} = window.Shopify || {};

  const {data} = await postData('/metafields/storefront', {
    t: translation,
    chattySat,
    shop: shop || shopifyDomain,
    websiteId
  });

  Object.assign(window.AVADA_FAQ, data);
  window.AVADA_FAQ.fetchMetafields = true;
}

/**
 * @param {function} postData
 * @returns {Promise<void>}
 */
export async function getAppStatusMetafields(postData) {
  if (Object.keys(window.AVADA_APP_FAQS_STATUS).length) return;

  const websiteId = window.AVADA_APP_WEBSITE_ID || '';
  const domain = window.shopify?.config?.shop;
  const {appStatus} = await postData('/metafields/app-status', {shop: domain, websiteId});

  window.Shopify = {shop: domain};
  window.AVADA_APP_FAQS_STATUS = appStatus;
}
